import { FunctionComponent, useState } from 'react';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useNotification } from '@starjack/features/notifications';
import { PriceCondition, ProductConfiguration } from '@/models/product';
import { Drawer, Stack, Typography } from '@mui/material';
import { ConditionPreviewList } from './ConditionPreviewList';
import {
    useGetProductConfigurationQuery,
    usePostOrPutConfigurationMutation,
} from '@/api/v2/productConfigurations';
import { PriceConditionFormContainer } from '@/pageContents/productConfiguration/ProductConfigurationDetail/conditions/PriceConditionFormContainer';

type PriceConditionConfigurationProps = {
    productConfiguration: ProductConfiguration;
};

export const PriceConditionConfiguration: FunctionComponent<
    PriceConditionConfigurationProps
> = ({ productConfiguration }) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { isLoading: getQueryLoading } = useGetProductConfigurationQuery({
        liftOperatorId,
        productId: productConfiguration.id,
    });
    const [apiAction, { isLoading: updateLoading }] =
        usePostOrPutConfigurationMutation({
            liftOperatorId,
            productConfiguration,
        });

    const isLoading = getQueryLoading || updateLoading;
    const { addNotification } = useNotification();

    const [conditionDialogConfig, setConditionDialogConfig] = useState<{
        isOpen: boolean;
        conditionTemplate: PriceCondition;
    }>({
        isOpen: false,
        conditionTemplate: null,
    });

    const updateConditionSort = (
        conditions: PriceCondition[],
        condition: PriceCondition,
        priorityChange: 'increase' | 'decrease'
    ) => {
        const cToUpdateIndex = conditions.findIndex(
            (c) => c.id === condition.id
        );

        if (cToUpdateIndex > -1) {
            const cToReplacedIndex =
                priorityChange === 'increase'
                    ? cToUpdateIndex - 1
                    : cToUpdateIndex + 1;

            if (conditions[cToReplacedIndex]) {
                const conditionToUpdate = { ...conditions[cToUpdateIndex] };
                const conditionToReplace = { ...conditions[cToReplacedIndex] };
                const toReplaceSort = conditionToReplace.sort;
                const toUpdateSort = conditionToUpdate.sort;

                conditions.splice(cToUpdateIndex, 1, {
                    ...conditionToUpdate,
                    sort: toReplaceSort,
                });

                conditions.splice(cToReplacedIndex, 1, {
                    ...conditionToReplace,
                    sort: toUpdateSort,
                });

                apiAction({
                    liftOperatorId,
                    productConfiguration: {
                        ...productConfiguration,
                        priceConditions: [...conditions],
                    },
                })
                    .unwrap()
                    .then(() => {
                        addNotification({
                            message: 'Sortierung der Regel aktualisiert',
                            type: 'success',
                        });
                    })
                    .catch(() => {
                        addNotification({
                            message:
                                'Fehler beim Speichern der neuen Sortierung. Bitte versuchen Sie es später noch einmal',
                            type: 'error',
                        });
                    });
            }
        }
    };

    const deleteCondition = (condition: PriceCondition) => {
        const conditions = [...productConfiguration.priceConditions];
        const conditionIndex = conditions.findIndex(
            (c) => c.id === condition.id
        );

        if (conditionIndex > -1) {
            conditions.splice(conditionIndex, 1);

            apiAction({
                liftOperatorId,
                productConfiguration: {
                    ...productConfiguration,
                    priceConditions: [...conditions],
                },
            })
                .then(() => {
                    addNotification({
                        message: 'Regel gelöscht',
                        type: 'success',
                    });
                })
                .catch(() => {
                    addNotification({
                        message: 'Fehler beim Löschen der Regel',
                        type: 'error',
                    });
                });
        }
    };

    return (
        <Stack spacing={3}>
            <Typography gutterBottom>
                Sie können mehrere Verfügbarkeits-Regeln hinterlegen. <br />
                Die Regeln kommen der Priorität nach zur Anwendung.
            </Typography>

            <Typography>
                Bitte beachten: Ein in der „allgemeinen Konfiguration“
                hinterlegtes Kontingent braucht sich durch diese Regeln
                ebenfalls auf.
            </Typography>

            <ConditionPreviewList
                conditions={productConfiguration.priceConditions}
                onCreateOrUpdate={(condition: PriceCondition) => {
                    setConditionDialogConfig({
                        isOpen: true,
                        conditionTemplate: condition || null,
                    });
                }}
                onDelete={deleteCondition}
                onPriorityChange={updateConditionSort}
                isLoading={isLoading}
                onAddClick={() => {
                    setConditionDialogConfig({
                        isOpen: true,
                        conditionTemplate: null,
                    });
                }}
            />

            <Drawer
                onClose={() => {
                    setConditionDialogConfig({
                        isOpen: false,
                        conditionTemplate: null,
                    });
                }}
                anchor='right'
                open={conditionDialogConfig.isOpen}
            >
                <PriceConditionFormContainer
                    onClose={() => {
                        setConditionDialogConfig((prevState) => ({
                            ...prevState,
                            isOpen: false,
                        }));
                    }}
                    condition={conditionDialogConfig.conditionTemplate}
                    productConfiguration={productConfiguration}
                />
            </Drawer>
        </Stack>
    );
};
